import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import ModalFullPage from '../../../Common/Components/ModalFullPage';
import { ScheduleModalProps } from '../../../types/ScheduleModalProps';
import { ScheduleObject } from '../../../types/ScheduleObject';
import { useAppContext } from '../../../utils/AppContext';
import ScheduleToolbar from './ScheduleToolbar';
import DimmingSequences from './DimmingSequences';
import DimmingPoints from './DimmingPoints';
import { deleteRequest, putRequest, postRequest } from '../../../utils/fetch';
import Utils from '../../../utils/Utils';

function ScheduleModal(props: ScheduleModalProps): JSX.Element {
  // eslint-disable-next-line max-len
  const { selectedCustomer, selectedSite, selectedDefaultCustomer, selectedDefaultSite, schedules, activeSchedule, openScheduleModal, setOpenScheduleModal, updateSchedules } = props;
  const { addNotification } = useAppContext();

  const [scheduleData, setScheduleData] = useState<ScheduleObject>(activeSchedule);
  const [selectedSequenceIndex, setSelectedSequenceIndex] = useState<number>(0);
  const [openToolbarDropdown, setOpenToolbarDropdown] = useState<Record<string, boolean>>({ open: false, nonUnique: false });
  const names = useMemo(
    () => schedules?.filter((schedule) => schedule.scheduleId !== scheduleData.scheduleId).map((schedule) => schedule.name),
    [schedules, scheduleData],
  );

  const modalScheduleCreate = 1;
  const modalScheduleEdit = 2;
  const modalScheduleView = 3;
  const daysOfWeek = 7;
  const maxSpecialDayEvents = 100;
  const isSensityUserAdmin = Utils.isSensityUserAdmin();

  const isReadOnly = openScheduleModal === modalScheduleView || (!isSensityUserAdmin && scheduleData.isDefault === true);

  const validateSchedule = (): boolean => {
    let valid = true;

    setOpenToolbarDropdown({ open: false });

    if (scheduleData.name === '') {
      setOpenToolbarDropdown({ open: true, nonUnique: false });
      return false;
    }

    if (names?.includes(scheduleData.name)) {
      setOpenToolbarDropdown({ open: true, nonUnique: true });
      return false;
    }

    const selectedDays: Set<string> = new Set();

    scheduleData.events.forEach((event, index) => {
      if (valid && !event.date && !event.days) {
        addNotification({ type: 'error', message: `Sequence ${index + 1} has no day or date specified.` });
        valid = false;
      }

      if (valid && event.actions.length === 0) {
        addNotification({ type: 'error', message: `Sequence ${index + 1} has no dimming points.` });
        valid = false;
      }

      if (event.days) {
        event.days.forEach((day) => selectedDays.add(day));
      }
    });

    if (valid && selectedDays.size !== daysOfWeek) {
      addNotification({ type: 'error', message: 'Schedule must contain a valid dimming sequence for each day of the week.' });
      return false;
    }

    if (scheduleData.events.filter((e) => e.date).length > maxSpecialDayEvents) {
      addNotification({ type: 'error', message: `Schedule cannot contain more than ${maxSpecialDayEvents} special day events.` });
      valid = false;
    }

    if (scheduleData.configuration
      && scheduleData.configuration.ph_lux_min
      && scheduleData.configuration.ph_lux_max
      && (scheduleData.configuration.ph_lux_min >= scheduleData.configuration.ph_lux_max)) {
      addNotification({ type: 'error', message: 'Min Lux value must be less than Max Lux value.' });
      valid = false;
    }

    return valid;
  };

  const saveSchedule = async () => {
    if (!isReadOnly && validateSchedule()) {
      let url = '';
      if ((activeSchedule.isDefault) && selectedDefaultCustomer && selectedDefaultSite) {
        url = `/organizations/${selectedDefaultCustomer}/sites/${selectedDefaultSite}/schedules`;
      } else {
        url = `/organizations/${selectedCustomer?.id}/sites/${selectedSite?.id}/schedules`;
      }

      if (openScheduleModal === modalScheduleEdit) {
        // edit schedule
        url += `/${scheduleData.scheduleId}`;
      }

      scheduleData.events = scheduleData.events.map((event) => {
        const newEvent = { ...event };

        if (newEvent.date && newEvent.date instanceof Date) {
          newEvent.date = DateTime.fromJSDate(newEvent.date).toFormat('yyyy-LL-dd');
        }

        newEvent.actions = newEvent.actions.map((action) => {
          const newAction = { ...action };

          if (newAction.time === '24:00:00') {
            newAction.time = '00:00:00';
          }

          return newAction;
        });

        return newEvent;
      });

      const operation = openScheduleModal === modalScheduleCreate ? 'Create' : 'Edit';

      try {
        let result;
        const request = {
          name: scheduleData.name,
          description: scheduleData.description,
          events: scheduleData.events,
          scheduleHints: scheduleData.scheduleHints,
          configuration: scheduleData.configuration,
        };
        if (operation === 'Create') {
          result = await postRequest(url, request);
        } else {
          result = await putRequest(url, request);
        }

        if (!result.error) {
          addNotification({ type: 'info', message: `Your "${operation} schedule" operation is completed.` });
          setOpenScheduleModal(0);

          if (updateSchedules) {
            updateSchedules();
          }
        } else {
          addNotification({ type: 'error', message: `Your "${operation} schedule" operation has failed: ${result.error}` });
        }
      } catch (e) {
        addNotification({ type: 'error', message: `Your "${operation} schedule" operation has failed.` });
      }
    }
  };

  const deleteSchedule = async () => {
    try {
      const result = await deleteRequest(`/organizations/${selectedCustomer?.id}/sites/${selectedSite?.id}/schedules/${scheduleData.scheduleId}`);

      if (!result.error) {
        addNotification({ type: 'info', message: 'Your "Delete schedule" operation is completed.' });
        setOpenScheduleModal(0);

        if (updateSchedules) {
          updateSchedules();
        }
      } else {
        addNotification({ type: 'error', message: `Your "Delete schedule" operation has failed: ${result.error}` });
      }
    } catch (e) {
      addNotification({ type: 'error', message: 'Your "Delete schedule" operation has failed.' });
    }
  };

  return (
    <ModalFullPage>
      <ScheduleToolbar
        scheduleData={scheduleData}
        setScheduleData={setScheduleData}
        openScheduleModal={openScheduleModal}
        setOpenScheduleModal={setOpenScheduleModal}
        saveSchedule={saveSchedule}
        deleteSchedule={deleteSchedule}
        openToolbarDropdown={openToolbarDropdown}
        selectedCustomer={selectedCustomer}
        isReadOnly={isReadOnly}
        names={names}
      />
      <DimmingSequences
        dimmingSequencesTitle="Dimming sequences"
        scheduleData={scheduleData}
        setScheduleData={setScheduleData}
        selectedSequenceIndex={selectedSequenceIndex}
        setSelectedSequenceIndex={setSelectedSequenceIndex}
        isReadOnly={isReadOnly}
      />
      <DimmingPoints
        scheduleData={scheduleData}
        setScheduleData={setScheduleData}
        selectedSequenceIndex={selectedSequenceIndex}
        isDefaultSite={selectedDefaultSite !== undefined}
        isReadOnly={isReadOnly}
      />
    </ModalFullPage>
  );
}

export default ScheduleModal;
